<template>
  <div class="campaign-card">
    <div class="header" :class="{ 'not-activated': !assessment.activated }">
      <div v-if="!assessment.activated" class="not-activated-content">
        <div class="tag black">
          <i class="bi bi-info-circle" />
          {{ $t("game.assessmentCard.notActivatedTag") }}
        </div>
        <p>
          {{ $t("game.assessmentCard.notActivatedDescription") }}
        </p>
      </div>
      <img :src="assessment?.campaign?.branding?.banner_image_id ? getImage(assessment.campaign.branding.banner_image_id) : getRandomCampaignPicture(assessment.campaign.campaign.headline)" alt="Campaign header" ref="image" />
    </div>
    <div class="body">
      <div class="name-duration-row">
        <div class="block">
          <h4 class="card-title" v-text="assessment.campaign.campaign.headline" />
          <p class="job" v-text="assessment.campaign.campaign.job_title" />
        </div>
        <div class="informations-container">
          <div class="tag-container">
            <div class="tag" :style="getThemeStyle(assessment.campaign)">{{ $t("game.assessmentCard.averageTimeTag", { duration }) }}</div>
          </div>
        </div>
      </div>
      <p class="campaign-description" v-safe-html="assessment.campaign.campaign.tagline" />
      <Button v-if="assessment.state !== 2" class="icon icon-left primary full-size" :style="getThemeStyle(assessment.campaign)" :disabled="!assessment.activated || (isMobile && !isMobileEnabled)" :label="btnLabel" :icon="assessment.state ? 'bi-rocket-takeoff' : 'bi-play-fill'" @click="emits('startAssessment')" />
      <Button v-else class="icon icon-left green full-size" :icon="canSeeResults ? 'bi-download' : undefined" :label="canSeeResults ? $t('game.assessmentCard.report.download') : $t('game.assessmentCard.report.none')" @click="canSeeResults ? getResults() : undefined" :loading="isPdfLoading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getImage, getRandomCampaignPicture } from "@/composables/getImage.js";
import { useCandidateStore } from "@/stores/candidate";
import { Participant } from "@/types/participant";
import { Campaign } from "@/types/campaign";
import { is } from "@vee-validate/rules";

const { t, locale } = useI18n();

const emits = defineEmits(["verifyPhone", "startAssessment", "setBGImage"]);

const candidateStore = useCandidateStore();

const { getAdventureDuration, isCampaignV1, isCampaignV2, isCampaignV3OrMore } = useSkillsStore();

const props = defineProps<{
  assessment: Participant,
  hasResults?: boolean,
  canStart?: boolean,
  isMobile: boolean,
  isMobileEnabled: boolean,
}>();

const isPdfLoading = ref(false);

const btnLabel = computed(() => {
  if (!props.isMobileEnabled && props.isMobile) return t("game.assessmentCard.mobileConnectionMessage");

  if (props.assessment.state === 1) return t("game.assessmentCard.resumeAssessmentButton");

  return t("game.assessmentCard.startAssessmentButton");
});

const canSeeResults = computed(() => {
  if (isCampaignV1(props.assessment.campaign)) return false;
  if (isCampaignV3OrMore(props.assessment.campaign) && props.assessment?.campaign?.documents?.report?.send_to_talent === false) return false;
  return true;
})

function getThemeStyle(campaign: Campaign) {
  if (!campaign) {
    return "";
  }

  var bgColour = "#f2cb36";
  var textColour = "#000000";

  return {
    "background-color": campaign?.branding?.highlight_color ? campaign?.branding?.highlight_color : bgColour,
    color: campaign?.branding?.text_color ? campaign?.branding?.text_color : textColour,
  };
}

async function getResults() {
  const newWindow = window.open('', '_blank');

  newWindow.document.write(`
    <html><body style="font-family:Helvetica;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">
      <p>${locale.value === 'en' ? 'Loading' : 'Chargement'}...</p>
    </body></html>
  `);
  try {
    isPdfLoading.value = true;
    let pdf;
    if (isCampaignV2(props.assessment?.campaign) || props.assessment?.campaign?.documents?.report.format === 'simplified') {
      pdf = await candidateStore.getSimplifiedReport(props.assessment.participant_id);
    } else {
      pdf = await candidateStore.getFullReport(props.assessment.participant_id);
    }
    const fileURL = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
    newWindow.location.href = fileURL;

    // Clean up
    setTimeout(() => URL.revokeObjectURL(fileURL), 100);
  } catch (error) {
    newWindow.close();
    console.error(error);
  } finally {
    isPdfLoading.value = false;
  }
}

const duration = computed<number>(() => {
  if (isCampaignV2(props.assessment?.campaign)) {
    if (props?.assessment?.campaign?.variant === 'essentials') return 25;
    return 50;
  } else if (isCampaignV1(props.assessment?.campaign)) {
    return 50;
  } else {
    return getAdventureDuration(props?.assessment?.campaign?.assessment.modules);
  }
});
</script>
